/** Every navigable path in Cyphex Teams. Never hardcode a path string. */
export const ROUTES = {
  root: '/',
  /** Signed-out surfaces. Everything else sits behind <RequireAuth>. */
  login: '/login',
  register: '/signup',
  /**
   * Landing page for an emailed external-channel invitation. PUBLIC by
   * necessity — the recipient has no account in THIS workspace, and a
   * cross-tenant invitee's account belongs to another one entirely. The token
   * in the path is the only credential involved.
   */
  invitePattern: '/invite/:token',
  invite: (token: string) => `/invite/${token}`,
  /**
   * A file share link. PUBLIC, and short on purpose — this path is pasted into
   * emails and chat by people outside the workspace, and every extra segment is
   * another chance for a mail client to wrap the line and break it.
   *
   * The API mints exactly this shape (`routes/files.ts`), so the two must not
   * drift: a mismatch here is a link that resolves to the SPA's not-found page
   * with a perfectly valid token in it.
   */
  sharePattern: '/s/:token',
  share: (token: string) => `/s/${token}`,
  activity: '/activity',
  chat: '/chat',
  chatThread: (conversationId: string) => `/chat/${conversationId}`,
  teams: '/teams',
  channel: (teamId: string, channelId: string) => `/teams/${teamId}/${channelId}`,
  /**
   * A persistent voice room. Its own path segment so it never collides with a
   * text channel id, and so a link into a voice room is unambiguous.
   */
  voiceChannel: (teamId: string, channelId: string) => `/teams/${teamId}/voice/${channelId}`,
  voiceChannelPattern: '/teams/:teamId/voice/:channelId',
  /**
   * Search results. The query lives in `?q=` (plus `scope` and `sort`) rather
   * than in a path segment so a result page is linkable, bookmarkable and
   * survives Back — and so operators like `in:#general` do not have to be
   * path-escaped.
   */
  search: '/search',
  searchFor: (query: string, scope?: string) =>
    `/search?q=${encodeURIComponent(query)}${scope === undefined || scope === 'all' ? '' : `&scope=${scope}`}`,
  calendar: '/calendar',
  calls: '/calls',
  /**
   * A LIVE call's media surface. Full-bleed and OUTSIDE <AppShell>, exactly as
   * `/meet/:meetingId` is: a call takes the whole window, and a sidebar is only
   * somewhere to click away from the person you are talking to. Singular, so it
   * can never be confused with the `/calls` history list.
   */
  call: (callId: string) => `/call/${callId}`,
  callPattern: '/call/:callId',
  /**
   * Meeting recordings, workspace-wide. A recording OUTLIVES the meeting it
   * came from, so it needs a durable home of its own rather than a panel that
   * disappears when the call ends.
   */
  recordings: '/recordings',
  files: '/files',
  tasks: '/tasks',
  /**
   * ONE TASK, OPEN. The id is in the PATH, not a query: every notification
   * about a task links here (`taskLink` in apps/api/src/services/taskNotify.ts
   * writes exactly this shape, so the two must not drift), and the desktop
   * client decides whether somebody is "already reading it" by comparing the
   * path alone. `/tasks?task=<id>` would have made every task the same place.
   * Which list is showing rides along as `?view=`.
   */
  task: (taskId: string) => `/tasks/${taskId}`,
  taskPattern: '/tasks/:taskId',
  apps: '/apps',
  /**
   * Webinars. Three surfaces, and the split matters:
   *   /webinars…            the ORGANISER console, behind <RequireAuth>
   *   /webinar/:slug        the PUBLIC registration page — a stranger's first
   *                         and possibly only contact with this workspace
   *   /webinar/:slug/live   the attendee's watch page, authenticated by the
   *                         `?t=` registration token and nothing else
   * Singular for the public pair so a reverse proxy or a WAF can tell the two
   * apart on the path alone.
   */
  webinars: '/webinars',
  webinarDetail: (webinarId: string) => `/webinars/${webinarId}`,
  webinarDetailPattern: '/webinars/:webinarId',
  webinarGreenRoom: (webinarId: string) => `/webinars/${webinarId}/green-room`,
  webinarGreenRoomPattern: '/webinars/:webinarId/green-room',
  webinarPublic: (slug: string) => `/webinar/${slug}`,
  webinarPublicPattern: '/webinar/:slug',
  webinarStage: (slug: string, token: string) =>
    `/webinar/${slug}/live?t=${encodeURIComponent(token)}`,
  webinarStagePattern: '/webinar/:slug/live',
  meeting: (meetingId: string) => `/meet/${meetingId}`,
  meetingPattern: '/meet/:meetingId',
  /**
   * REDEEMING A JOIN CODE — the URL this product has always emitted and never
   * served.
   *
   * `apps/api/src/db/map/meeting.ts` puts `${WEB_BASE_URL}/join/<code>` in the
   * `joinUrl` of EVERY meeting DTO, `routes/meetings.ts` puts the same string
   * in the `URL:` line and the body of every ICS it builds, and
   * `routes/calendar.ts` returns it as `joinUrl` from `/view` and `/upcoming`.
   * Until this entry existed there was no `/join` path in the router at all, so
   * every one of them fell through to the "Not found" scaffold.
   *
   * The code, not the meeting id, precisely because `/meet/:meetingId` refuses
   * anyone who is not already entitled ("A MEETING ID IS NOT A CREDENTIAL",
   * routes/meetings.ts). Forwarding an invitation is what this path is for.
   *
   * Bare `/join` is the same page with an empty box, so "Join with a code" and
   * a pasted link land on one surface.
   */
  join: (joinCode: string) => `/join/${encodeURIComponent(joinCode)}`,
  joinEntry: '/join',
  joinPattern: '/join/:joinCode',
  /**
   * The admin console. Rendered OUTSIDE <AppShell>: it is a different product
   * surface with its own nav, and an operator must be able to reach it when the
   * shell's own data (sidebar, presence) is what is broken.
   */
  admin: '/admin',
  adminUsers: '/admin/users',
  adminTeams: '/admin/teams',
  adminGroups: '/admin/groups',
  adminPolicies: '/admin/policies',
  adminSecurity: '/admin/security',
  /**
   * Federation and provisioning: OIDC/SAML providers, SCIM tokens, and the
   * deprovisioning trail. Separate from `adminSecurity` because that screen is
   * about the workspace's OWN credentials policy, and this one is about handing
   * authentication to somebody else's directory.
   */
  adminIdentity: '/admin/identity',
  /** Cross-org connections, external members, and the guest kill switch. */
  adminExternal: '/admin/external',
  /**
   * Telephony: SIP trunks, the number inventory, call queues, auto-attendants
   * and the call detail records. Its own console section rather than a tab of
   * Policies — a trunk credential is a bearer token for somebody else's phone
   * network and belongs behind its own permission.
   */
  adminPstn: '/admin/telephony',
  adminAudit: '/admin/audit',
  adminCompliance: '/admin/compliance',
  adminOrg: '/admin/org',
  /**
   * Self-service security: your devices, your second factor, your password.
   * Inside the shell and NOT under /admin — every control acts on your own
   * account and needs no administrative permission.
   */
  security: '/security',
  /** Your own profile - photo, name, title, status. */
  profile: '/profile',
  /** Every per-person setting in one place. `?section=` picks the tab. */
  settings: '/settings',
} as const;

/** Console sections, in nav order. */
export const ADMIN_SECTIONS = [
  'overview',
  'users',
  'teams',
  'policies',
  'security',
  'identity',
  'external',
  'telephony',
  'audit',
  'compliance',
  'org',
] as const;

export type AdminSection = (typeof ADMIN_SECTIONS)[number];

/** Order of the far-left icon rail, top to bottom. `help` is pinned to the bottom. */
export const RAIL_ORDER = [
  'activity',
  'chat',
  'teams',
  'calendar',
  'calls',
  'recordings',
  'files',
  'tasks',
  'webinars',
  'apps',
] as const;

export type RailKey = (typeof RAIL_ORDER)[number];
